/**
 * NOTE (Noah, 2026-02-03): useReploAgentConnection hook
 *
 * This hook manages a single WebSocket connection to the ReploAgent Durable Object.
 * It should only be called once in CurrentChatProvider to prevent multiple connections
 * from racing and canceling each other.
 */

/* biome-ignore-all lint/suspicious/noConsole: Development logging for agent debugging */
import type {
  AgentStateSyncPayload,
  SessionPromptFile,
  StreamingSessionState,
} from "schemas/generated/agentConnection";
import type {
  SemanticFileEditElement,
  SemanticPreviewElement,
} from "schemas/generated/semanticElement";

import { useCallback, useMemo, useState } from "react";

import { getAgentUrl } from "@agent-ui/features/chat/components/env";
import { getWebSocketErrorMessage } from "@agent-ui/features/chat/utils/websocketErrors";
import { useDebugMemoryStatsActions } from "@agent-ui/store/debugMemoryStatsStore";
import { useDebugSandboxProcessLogsActions } from "@agent-ui/store/debugSandboxProcessLogsStore";
import { useAgent } from "agents/react";
import { parseJsonOrNull } from "replo-utils/lib/json";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { agentToClientMessageSchema } from "schemas/agentConnection";

type PublishSyncStatus = NonNullable<AgentStateSyncPayload["publishStatus"]>;

// NOTE (Yuxin, 2026-01-31): State synced from the DO (flattened per-connection view)
// NOTE (Noah, 2026-02-22): streamingSessionState is keyed by sessionUuid,
// values contain streaming details (e.g. previewUrl, sandboxStatus)
export interface AgentState {
  streamingSessionState?: Record<string, StreamingSessionState>;
  publishStatus?: PublishSyncStatus;
  durableObjectId?: string;
}
export interface GitCommit {
  sha: string;
  shortSha: string;
  message: string;
  author: string;
  authorEmail: string;
  timestamp: string;
  relativeTime: string;
}

export interface SandboxFile {
  route: string; // "/about"
  filepath: string; // "app/about/page.tsx"
  modifiedAt?: number; // Unix timestamp in ms
  createdAt?: number; // Unix timestamp in ms
}

export type ReploAgent = ReturnType<typeof useAgent<AgentState>>;

export type AgentConnectionState = "connecting" | "connected" | "disconnected";

function getAgentName(projectId: string): string {
  return JSON.stringify({ type: "project", projectId });
}

export function useReploAgentConnection(opts: {
  projectId: string;
  agentToken: string;
  onError?: (error: Error) => void;
}) {
  const { projectId, agentToken, onError } = opts;
  const [agentState, setAgentState] = useState<AgentState>({});
  const [connectionState, setConnectionState] =
    useState<AgentConnectionState>("connecting");

  const { addLog } = useDebugSandboxProcessLogsActions();
  const { addSnapshot } = useDebugMemoryStatsActions();

  const handleError = useCallback(
    (error: unknown) => {
      const errorMessage = getWebSocketErrorMessage(error);
      onError?.(new Error(errorMessage));
    },
    [onError],
  );

  // NOTE (Gabe, 2026-02-19): The Agents framework broadcasts the full DO state
  // (DurableObjectInstanceState) via CF_AGENT_STATE on every this.setState() call.
  // This includes internal fields (connections, supervisor tokens, etc.) and
  // potentially stale data from previous sessions persisted in SQLite. We must NOT
  // use it to set agentState — only the per-connection "state-sync" messages from
  // handleMessage contain the correct client-facing state.
  const handleStateUpdate = useCallback((_newState: AgentState) => {
    console.log(
      "[useReploAgentConnection] Ignoring CF_AGENT_STATE broadcast (using state-sync messages only)",
    );
  }, []);

  const url = new URL(getAgentUrl());
  const isSecure = url.protocol === "https:";

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      console.log(
        "[useReploAgentConnection] Message received in useReploAgentConnection:",
        event,
      );
      const json = parseJsonOrNull(event.data as string);
      const parsed = agentToClientMessageSchema.safeParse(json);
      if (!parsed.success) {
        return;
      }

      const data = parsed.data;
      exhaustiveSwitch(data)({
        "state-sync": (msg) => {
          const hasStreamingSessionState = "streamingSessionState" in msg.state;

          setAgentState((prevState) => ({
            ...prevState,
            ...msg.state,
            ...(hasStreamingSessionState
              ? { streamingSessionState: msg.state.streamingSessionState }
              : {}),
          }));
        },
        "process-log": (msg) => {
          // NOTE (Noah, 2026-02-19, REPL-23937): Forward process logs to the zustand store
          // for display in the debug panel
          addLog(msg.payload);
        },
        "opencode-event": () => {
          // NOTE (Noah, 2026-02-20): OpenCode events are handled by onEvent callback
          // in useReploAgent, not here. This case is intentionally empty.
        },
        "memory-stats": (msg) => {
          addSnapshot(msg.payload);
        },
      });
    },
    [addLog, addSnapshot],
  );

  const handleOpen = useCallback(() => {
    setConnectionState("connected");
  }, []);

  const handleClose = useCallback(() => {
    setConnectionState("disconnected");
  }, []);

  const agent = useAgent({
    agent: "replo-agent",
    name: getAgentName(projectId),
    host: url.host,
    protocol: isSecure ? "wss" : "ws",
    query: { token: agentToken },
    // NOTE (Max, 2026-03-04): PartySocket's default connectionTimeout
    // is 4s, which is too short for DO cold starts after wrangler restarts. The timeout
    // fires before the handshake completes, creating a zombie socket on the server while
    // PartySocket reconnects with the same _pk — causing "More than one connection found".
    connectionTimeout: 30_000,
    onOpen: handleOpen,
    onClose: handleClose,
    onError: handleError,
    onMessage: handleMessage,
    onStateUpdate: handleStateUpdate,
  });

  const sessionCreate = useCallback(
    async ({ sessionUuid }: { sessionUuid: string }) => {
      const result = await agent.call<{
        sessionId: string;
        sessionUuid: string;
      }>("sessionCreate", [{ sessionUuid }]);
      return result;
    },
    [agent],
  );

  const sessionPrompt = useCallback(
    async (params: {
      sessionId: string;
      prompt: string;
      sessionUuid: string;
      systemContext: string;
      files: SessionPromptFile[];
    }) => {
      console.log(
        "[useReploAgent] Sending prompt to session:",
        params.sessionUuid,
      );
      await agent.call("sessionPrompt", [params]);
    },
    [agent],
  );

  const answerQuestion = useCallback(
    async (params: {
      sessionUuid: string;
      questionId: string;
      answers: string[][];
    }) => {
      await agent.call("answerQuestion", [params]);
    },
    [agent],
  );

  const streamMessages = useCallback(
    async (params: { sessionId: string; sessionUuid: string }) => {
      console.log(
        "[useReploAgent] Starting stream for session:",
        params.sessionId,
      );
      await agent.call("streamMessages", [params]);
    },
    [agent],
  );

  const sessionAbort = useCallback(
    async ({ sessionUuid }: { sessionUuid: string }) => {
      await agent.call<{ ok: boolean }>("sessionAbort", [{ sessionUuid }]);
    },
    [agent],
  );

  const getGitHistory = useCallback(
    async ({ limit = 20, skip = 0 }: { limit?: number; skip?: number }) => {
      console.log("[useReploAgent] Getting git history:", { limit, skip });
      const result = await agent.call<{
        commits: GitCommit[];
        hasMore: boolean;
      }>("getGitHistory", [{ limit, skip }]);
      console.log(
        "[useReploAgent] Git history fetched:",
        result.commits.length,
      );
      return result;
    },
    [agent],
  );

  const revertToCommit = useCallback(
    async ({ sha }: { sha: string }) => {
      console.log("[useReploAgent] Reverting to commit:", sha);
      const result = await agent.call<{ ok: boolean }>("revertToCommit", [
        { sha },
      ]);
      console.log("[useReploAgent] Reverted:", result);
      return result;
    },
    [agent],
  );

  const previewCommit = useCallback(
    async ({ sha, sessionUuid }: { sha: string; sessionUuid: string }) => {
      console.log(
        "[useReploAgent] Previewing commit:",
        sha,
        "for session:",
        sessionUuid,
      );
      const result = await agent.call<{ ok: boolean }>("previewCommit", [
        { sha, sessionUuid },
      ]);
      console.log("[useReploAgent] Preview started:", result);
      return result;
    },
    [agent],
  );

  const exitPreview = useCallback(
    async ({ sessionUuid }: { sessionUuid: string }) => {
      console.log("[useReploAgent] Exiting preview for session:", sessionUuid);
      const result = await agent.call<{ ok: boolean }>("exitPreview", [
        { sessionUuid },
      ]);
      console.log("[useReploAgent] Preview exited:", result);
      return result;
    },
    [agent],
  );

  const listFiles = useCallback(async () => {
    console.log("[useReploAgent] Listing files");
    const result = await agent.call<{ files: SandboxFile[] }>("listFiles", []);
    console.log("[useReploAgent] Files listed:", result.files.length);
    return result;
  }, [agent]);

  const createFile = useCallback(
    async ({ route }: { route: string }) => {
      console.log("[useReploAgent] Creating file:", route);
      const result = await agent.call<{ ok: boolean }>("createFile", [
        { route },
      ]);
      console.log("[useReploAgent] File created:", result);
      return result;
    },
    [agent],
  );

  const renameFile = useCallback(
    async ({ fromRoute, toRoute }: { fromRoute: string; toRoute: string }) => {
      console.log("[useReploAgent] Renaming file:", fromRoute, "->", toRoute);
      const result = await agent.call<{ ok: boolean }>("renameFile", [
        { fromRoute, toRoute },
      ]);
      console.log("[useReploAgent] File renamed:", result);
      return result;
    },
    [agent],
  );

  const duplicateFile = useCallback(
    async ({ route, newRoute }: { route: string; newRoute: string }) => {
      console.log("[useReploAgent] Duplicating file:", route, "->", newRoute);
      const result = await agent.call<{ ok: boolean }>("duplicateFile", [
        { route, newRoute },
      ]);
      console.log("[useReploAgent] File duplicated:", result);
      return result;
    },
    [agent],
  );

  const deleteFile = useCallback(
    async ({ route }: { route: string }) => {
      console.log("[useReploAgent] Deleting file:", route);
      const result = await agent.call<{ ok: boolean }>("deleteFile", [
        { route },
      ]);
      console.log("[useReploAgent] File deleted:", result);
      return result;
    },
    [agent],
  );

  const resetProject = useCallback(async () => {
    const result = await agent.call<{ ok: boolean; message: string }>(
      "resetProject",
      [],
    );
    return result;
  }, [agent]);

  const publish = useCallback(async () => {
    await agent.call("publish", []);
  }, [agent]);

  const getPublishLogs = useCallback(async () => {
    return agent.call<{
      build?: { stdout: string; stderr: string };
      publish?: { stdout: string; stderr: string };
    } | null>("getPublishLogs", []);
  }, [agent]);

  const getSemanticPreview = useCallback(
    async ({
      element,
      instruction,
    }: {
      element: SemanticPreviewElement;
      instruction: string;
    }) => {
      const result = await agent.call<{
        newHtml: string;
        affectedSelectors: string[];
        error?: string;
        containerOpeningTag?: string;
      }>("getSemanticPreview", [{ element, instruction }]);
      return result;
    },
    [agent],
  );

  const queueSemanticFileEdit = useCallback(
    async ({
      elements,
      instruction,
      pageRoute,
      parentSessionUuid,
    }: {
      elements: SemanticFileEditElement[];
      instruction: string;
      pageRoute: string;
      parentSessionUuid: string;
    }) => {
      await agent.call<void>("queueSemanticFileEdit", [
        { elements, instruction, pageRoute, parentSessionUuid },
      ]);
    },
    [agent],
  );

  const actions = useMemo(() => {
    return {
      sessionCreate,
      sessionPrompt,
      answerQuestion,
      streamMessages,
      sessionAbort,
      getGitHistory,
      revertToCommit,
      previewCommit,
      exitPreview,
      listFiles,
      createFile,
      renameFile,
      duplicateFile,
      deleteFile,
      resetProject,
      publish,
      getPublishLogs,
      getSemanticPreview,
      queueSemanticFileEdit,
    };
  }, [
    sessionCreate,
    sessionPrompt,
    answerQuestion,
    streamMessages,
    sessionAbort,
    getGitHistory,
    revertToCommit,
    previewCommit,
    exitPreview,
    listFiles,
    createFile,
    renameFile,
    duplicateFile,
    deleteFile,
    resetProject,
    publish,
    getPublishLogs,
    getSemanticPreview,
    queueSemanticFileEdit,
  ]);

  return {
    state: agentState,
    agent,
    connectionState,
    actions,
  };
}
